import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { Trans, useTranslation } from "react-i18next";

import { useMutation, useQuery } from "@tanstack/react-query";
import { add } from "date-fns";
import { TimeInput } from "semantic-ui-calendar-react";

import {
  formatDate,
  formatDateAt,
  toClearableApiDateTime,
} from "simplydo/core/dates";
import { asMutation, initUseQuery } from "simplydo/core/query";
import SearchParams from "simplydo/core/searchParams";
import type { OpenAPI } from "simplydo/interfaces";
import { CheckboxHeader } from "simplydo/react/components/CheckboxHeader";
import { RichText, RichTextViewer } from "simplydo/react/richtext";
import { styled } from "simplydo/react/styled";
import { Button, Divider, Icon, Message } from "simplydo/react/ui";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { ChallengeChooser } from "components/lib/Choosers";
import { DateInput } from "components/lib/DateInputs";
import TimezoneExplainer from "components/lib/TimezoneExplainer";

import type { ChallengeDetail, ChallengeQuestion } from "../../types";
import QuestionList from "./QuestionList";
import { QuestionModal } from "./QuestionModal";

const SubmissionInputDiv = styled.div`
  display: ${({ theme }) => (theme.sizes.isMobile ? "block" : "flex")};
  flex-direction: row;
  align-items: center;
`;

type QuestionsProps = { challenge: ChallengeDetail; nonAdminView?: boolean };

type EditableQuestion =
  | ChallengeQuestion
  | (Partial<ChallengeQuestion> & {
      challenge: string;
      question?: string;
      answer?: string;
    });

const Questions = ({ challenge, nonAdminView }: QuestionsProps) => {
  const { t } = useTranslation();
  const challengeId = challenge._id;
  const [deadlineDate, setDeadlineDate] = useState("");
  const [deadlineTime, setDeadlineTime] = useState("00:00");
  const [questionsIntro, setQuestionsIntro] = useState(
    challenge.questionsIntro,
  );
  const [questionToEdit, setQuestionToEdit] = useState<EditableQuestion | null>(
    null,
  );
  const [selectedQuestions, setSelectedQuestions] = useState<string[]>([]);
  const questionQuery = useQuery(
    initUseQuery(api.challenges.getQuestions, [challenge._id]),
  );
  const user = useAppSelector((state) => state.user);

  useEffect(() => {
    if (!challenge.questionDeadline) {
      setDeadlineDate(formatDate(add(new Date(), { days: 7 }), "yyyy-MM-dd"));
    } else {
      setDeadlineDate(formatDate(challenge.questionDeadline, "yyyy-MM-dd"));
      setDeadlineTime(formatDate(challenge.questionDeadline, "HH:mm"));
    }
  }, [challenge]);

  const updateChallengeMutation = useMutation({
    mutationFn: asMutation(api.challenges.update)!,
    onSuccess: (data) => {
      api.setQueryData(api.challenges.get, [challengeId], (prev) => ({
        ...prev,
        ...data,
      }));
    },
  });

  const copyQuestionsFromChallengeMutation = useMutation({
    mutationFn: asMutation(api.challenges.copyQuestions)!,
    onSuccess: () => {
      toast.success(t("challenge.questions.copySuccess"));
      api.invalidateQueries(api.challenges.get, [challengeId]);
      api.invalidateQueries(api.challenges.getQuestions, [challengeId]);
    },
  });

  const canManageQuestions =
    !nonAdminView &&
    (util.hasPermission(user, "challenge.manageQuestions", challenge._id) ||
      util.hasPermission(user, "org.manageChallenges", challenge.organisation));

  const nonPublicQuestions = useMemo(
    () =>
      user
        ? (questionQuery.data?.questions?.filter((q) => !q.published) ?? [])
        : [],
    [user, questionQuery.data],
  );
  const publishedQuestions = useMemo(
    () => questionQuery.data?.questions?.filter((q) => q.published) ?? [],
    [questionQuery.data],
  );

  const setNewDeadline = useCallback(
    (enable: boolean, nextDeadlineDate: string, nextDeadlineTime: string) => {
      const dateAndTime = `${nextDeadlineDate} ${nextDeadlineTime}`;
      updateChallengeMutation.mutate([
        challengeId,
        { questionDeadline: toClearableApiDateTime(enable, dateAndTime) },
      ]);

      api.setQueryData(api.challenges.get, [challengeId], (prev) => ({
        ...prev,
        questionDeadline: enable
          ? new Date(dateAndTime).toISOString()
          : undefined,
      }));
    },
    [challengeId, updateChallengeMutation],
  );

  const updateDeadlineDate = useCallback(
    (nextDeadlineDate: string) => {
      if (challenge.questionDeadline) {
        setDeadlineDate(nextDeadlineDate);
        setNewDeadline(true, nextDeadlineDate, deadlineTime);
      } else setDeadlineDate(nextDeadlineDate);
    },
    [challenge.questionDeadline, setNewDeadline, deadlineTime],
  );

  const updateDeadlineTime = useCallback(
    (nextDeadlineTime: string) => {
      if (challenge.questionDeadline) {
        setDeadlineTime(nextDeadlineTime);
        setNewDeadline(true, deadlineDate, nextDeadlineTime);
      } else setDeadlineTime(nextDeadlineTime);
    },
    [challenge.questionDeadline, deadlineDate, setNewDeadline],
  );

  const announceQuestionMutation = useMutation({
    mutationKey: ["challenges", challengeId, "questions", "announce"],
    mutationFn: asMutation(api.challenges.announceQuestions)!,
    onSuccess: () => {
      setSelectedQuestions([]);
      toast.success("Users notified successfully");
    },
  });

  const announceQuestions = useCallback(
    (questionIds: string[]) => {
      announceQuestionMutation.mutate([
        challengeId,
        { questionIds: questionIds },
      ]);
    },
    [announceQuestionMutation, challengeId],
  );

  const createQuestionMutation = useMutation({
    mutationKey: ["challenges", challengeId, "questions"],
    mutationFn: asMutation(api.challenges.createQuestion)!,
    onSuccess: (result) => {
      api.setQueryData(
        api.challenges.getQuestions,
        [challengeId],
        (prev) => {
          return {
            ...prev,
            questions: [...(prev?.questions || []), result.question],
          };
        },
        { allowUndefined: true },
      );
    },
  });

  const updateQuestionMutation = useMutation({
    mutationKey: ["challenges", challengeId, "questions"],
    mutationFn: asMutation(api.challenges.updateQuestion)!,
    onSuccess: ({ question }) => {
      api.setQueryData(api.challenges.getQuestions, [challengeId], (prev) => ({
        ...prev,
        questions: prev.questions.map((q) =>
          q._id === question._id ? question : q,
        ),
      }));
    },
  });

  const publishQuestionMutation = useMutation({
    mutationKey: ["challenges", challengeId, "questions", "publish"],
    mutationFn: asMutation(api.challenges.publishQuestion)!,
    onSuccess: (result) => {
      api.invalidateQueries(api.challenges.get, [challengeId]);
      api.setQueryData(api.challenges.getQuestions, [challengeId], (prev) => ({
        ...prev,
        questions: prev.questions.map((q) =>
          q._id === result.question._id ? result.question : q,
        ),
      }));
    },
  });

  const saveAndMakePublic = (
    question: EditableQuestion,
    notifyUsers: boolean,
  ) => {
    if (question._id) {
      updateQuestionMutation.mutate(
        [
          challengeId,
          question._id,
          { question: question.question, answer: question.answer },
        ],
        {
          onSuccess: (result) => {
            publishQuestionMutation.mutate([challengeId, result.question._id]);
            setQuestionToEdit(null);
            if (notifyUsers) {
              announceQuestions([result.question._id]);
            }
          },
        },
      );
    } else {
      createQuestionMutation.mutate(
        [
          challengeId,
          { question: question.question ?? "", answer: question.answer },
        ],
        {
          onSuccess: (result) => {
            publishQuestionMutation.mutate([challengeId, result.question._id]);
            if (notifyUsers) {
              announceQuestions([result.question._id]);
            }
            setQuestionToEdit(null);
          },
        },
      );
    }
  };

  const publishFromList = useCallback(
    (question: ChallengeQuestion, notifyUsers: boolean) => {
      publishQuestionMutation.mutate([challengeId, question._id], {
        onSuccess: () => {
          if (notifyUsers) {
            announceQuestions([question._id]);
          }
        },
      });
    },
    [publishQuestionMutation, challengeId, announceQuestions],
  );

  const saveQuestion = (question: EditableQuestion) => {
    if (question._id) {
      updateQuestionMutation.mutate([
        challengeId,
        question._id,
        { question: question.question, answer: question.answer },
      ]);
    } else {
      createQuestionMutation.mutate([
        challengeId,
        { question: question.question ?? "", answer: question.answer },
      ]);
    }
  };

  return (
    <div>
      {questionToEdit ? (
        <QuestionModal
          question={questionToEdit}
          onClose={() => setQuestionToEdit(null)}
          canManageQuestions={canManageQuestions}
          saveAndMakePublic={saveAndMakePublic}
          onSave={saveQuestion}
          isSaving={
            createQuestionMutation.isPending || updateQuestionMutation.isPending
          }
          isPublishing={publishQuestionMutation.isPending}
        />
      ) : null}
      <h3>{t("challenge.questions.clarificationQuestions")}</h3>
      {!nonAdminView && canManageQuestions ? (
        <div
          style={{ display: "flex", flexDirection: "column", marginTop: 10 }}
        >
          <p>
            <h5>{t("challenge.questions.introductionTitle")}</h5>
            {t("challenge.questions.introductionDescription")}
          </p>
          <RichText
            forType="challenge"
            forId={challengeId}
            value={questionsIntro ?? ""}
            emptyReturnValue={""}
            onChange={(data) => {
              setQuestionsIntro(data);
            }}
            placeholder={
              t("challenge.questions.defaultHelpText.default") +
              " " +
              challenge.questionSubmissionsEnabled
                ? t("challenge.questions.defaultHelpText.submissionsEnabled")
                : t("challenge.questions.defaultHelpText.submissionsDisabled")
            }
          />
          {questionsIntro && questionsIntro !== challenge?.questionsIntro ? (
            <Button
              primary
              content={t("challenge.questions.introductionSave")}
              size="small"
              icon="save"
              style={{ alignSelf: "flex-start", marginTop: 12 }}
              onClick={() => {
                api.setQueryData(api.challenges.get, [challengeId], (prev) => ({
                  ...prev,
                  questionsIntro,
                }));
                updateChallengeMutation.mutate([
                  challengeId,
                  { questionsIntro },
                ]);
                toast.success(t("challenge.questions.introductionSaveSuccess"));
              }}
            />
          ) : null}
          <Divider hidden />
          <CheckboxHeader
            checked={!!challenge.questionDeadline}
            header={t("challenge.questions.deadline")}
            onChange={() =>
              setNewDeadline(
                !challenge.questionDeadline,
                deadlineDate,
                deadlineTime,
              )
            }
            willHideChildren
            style={{ marginTop: 10 }}
            as="h5"
            description={`Set a deadline date for submission of new questions to this ${t("generic.challenge")}. If a deadline date is
            set, question submission will automatically close at this point.`}
          >
            <SubmissionInputDiv>
              New questions will be disabled on{" "}
              <DateInput
                clearable
                name="date"
                placeholder="Date"
                value={deadlineDate}
                minDate={new Date()}
                dateFormat="yyyy-MM-dd"
                iconPosition="left"
                onChange={(_e, { value }) =>
                  updateDeadlineDate(
                    formatDate(new Date(value ?? ""), "yyyy-MM-dd"),
                  )
                }
                onClear={() =>
                  updateDeadlineDate(formatDate(undefined, "yyyy-MM-dd"))
                }
                style={{ marginLeft: 10, marginRight: 10 }}
              />
              {" at "}
              <TimeInput
                clearable
                name="time"
                placeholder="Time"
                value={deadlineTime}
                iconPosition="left"
                onChange={(_e, { value }) => updateDeadlineTime(value ?? "")}
                onClear={() => updateDeadlineTime("00:00")}
                style={{ marginLeft: 10, marginRight: 10 }}
              />
            </SubmissionInputDiv>
            <TimezoneExplainer style={{ marginTop: 10 }} />
          </CheckboxHeader>
        </div>
      ) : challenge.questionsIntro ? (
        <>
          <RichTextViewer value={challenge.questionsIntro} />
          <Divider hidden />
        </>
      ) : challenge.questionSubmissionsEnabled ? (
        <p>{t("challenge.questions.defaultHelpText.submissionsEnabled")}</p>
      ) : (
        <p>{t("challenge.questions.defaultHelpText.submissionsDisabled")}</p>
      )}
      {challenge.questionDeadline ? (
        <Message info size="small">
          <p>
            <Trans
              i18nKey="challenge.questions.deadlineWarning"
              values={{ deadline: formatDateAt(challenge.questionDeadline) }}
            />
          </p>
        </Message>
      ) : null}
      <Divider hidden />

      {challenge.questionSubmissionsEnabled || canManageQuestions ? (
        <div>
          <Button
            style={{ marginBottom: 16 }}
            primary
            onClick={() => {
              if (!user) {
                const searchParams = new SearchParams(window.location.search);
                const redirectPath = `/login?utm-challenge=${challengeId}${searchParams.toSafeString("&")}&then=/challenges/${challengeId}/questions`;
                if (challenge?.ownerOrganisation) {
                  window.location.href = util.getBaseUrl(
                    challenge.ownerOrganisation,
                    redirectPath,
                  );
                } else {
                  window.location.href = redirectPath;
                }
                return;
              }
              setQuestionToEdit({
                challenge: challenge._id,
                question: "",
                answer: "",
              });
            }}
          >
            <Icon name="plus" />
            {canManageQuestions
              ? t("challenge.questions.addQuestion")
              : t("challenge.questions.askQuestion")}
          </Button>
          {canManageQuestions ? (
            <ChallengeChooser
              trigger={
                <Button style={{ marginBottom: 16 }}>
                  <Icon name="copy" />
                  Copy questions from another {t("generic.challenge")}
                </Button>
              }
              single
              onComplete={(fromChallenge) => {
                const sourceChallengeId = fromChallenge[0]?._id;
                if (!sourceChallengeId) return;
                copyQuestionsFromChallengeMutation.mutate([
                  challengeId,
                  sourceChallengeId,
                ]);
              }}
              forType="questions"
              ignores={[challengeId]}
              beforeContent={`Choose ${t("generic.challengeWithArticle")} to copy questions from. You can view, edit and delete them after copying.`}
              isLoading={copyQuestionsFromChallengeMutation.isPending}
              confirm={`Copy from this ${t("generic.challenge")}`}
            />
          ) : null}
        </div>
      ) : null}
      {nonPublicQuestions.length > 0 ? (
        <div style={{ marginBottom: 15, marginTop: 15 }}>
          <h5>
            {canManageQuestions
              ? `${t("challenge.questions.nonPublicQuestions")} (${nonPublicQuestions.length})`
              : t("challenge.questions.questionsAwaitingAnswe")}
          </h5>
          <QuestionList
            questions={nonPublicQuestions}
            challenge={challenge}
            setQuestionToEdit={
              canManageQuestions ? setQuestionToEdit : undefined
            }
            saveAndMakePublic={publishFromList}
            nonAdminView={!!nonAdminView}
          />
        </div>
      ) : null}
      <Divider hidden />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <h5>
          {canManageQuestions
            ? `${t("challenge.questions.publicQuestions")} (${publishedQuestions?.length || 0})`
            : t("challenge.questions.allQuestions")}
        </h5>
        {canManageQuestions ? (
          <Button
            loading={announceQuestionMutation.isPending}
            disabled={selectedQuestions.length === 0}
            icon="bell"
            primary
            content="Notify users about selected questions"
            onClick={() => announceQuestions(selectedQuestions)}
            size="small"
          />
        ) : null}
      </div>
      {publishedQuestions?.length === 0 ? (
        <Message info style={{ marginTop: 0 }}>
          <Message.Header>
            {t("challenge.questions.noQuestionsTitle")}
          </Message.Header>
          <Message.Content>
            {t("challenge.questions.noQuestionsDescription")}
          </Message.Content>
        </Message>
      ) : (
        <QuestionList
          selectedQuestions={selectedQuestions}
          setSelectedQuestions={setSelectedQuestions}
          questions={publishedQuestions}
          challenge={challenge}
          setQuestionToEdit={canManageQuestions ? setQuestionToEdit : undefined}
          nonAdminView={!!nonAdminView}
        />
      )}
    </div>
  );
};

export default Questions;
